import { RemoteFetchError, type ShareDecodeError } from '@nowline-app/share';

interface NoticeProps {
    /** Load error, if any. When null the banner shows a generic "errors found" message. */
    error: ShareDecodeError | RemoteFetchError | null;
    onReset: () => void;
}

/**
 * App-shell banner shown when the initial source failed to load or the
 * loaded diagram has errors. Provides a **Reset to example** button and
 * a "paste manually" hint for CORS failures.
 */
export function Notice({ error, onReset }: NoticeProps) {
    const isCors =
        error instanceof RemoteFetchError &&
        (error.reason === 'cors' || error.reason === 'network');

    let message: string;
    if (error === null) {
        message = 'The loaded diagram has errors.';
    } else if (isCors) {
        message = 'Could not load the shared diagram — network or CORS error.';
    } else if (error instanceof RemoteFetchError && error.reason === 'non-2xx') {
        message = `Could not load the shared diagram (HTTP ${error.status ?? 'error'}).`;
    } else if (error instanceof RemoteFetchError && error.reason === 'rejected-scheme') {
        message = 'Could not load the shared diagram — only https: URLs are supported.';
    } else {
        message = 'Could not decode the shared diagram.';
    }

    return (
        <div className='notice' role='alert' aria-live='polite'>
            <span className='notice-message'>{message}</span>
            {isCors && <span className='notice-hint'>Paste the source manually to continue.</span>}
            <button className='notice-reset' type='button' onClick={onReset}>
                Reset to example
            </button>
        </div>
    );
}
